/**
 * ConfirmDialog — Standardized confirmation modal
 *
 * Centered modal with backdrop. Title phrased as question.
 * Destructive variant (red) or default (teal).
 * Replaces DeleteArtworkModal and shadcn AlertDialog.
 */

import { useEffect, useCallback } from 'react';
import { T } from '@/styles/tokens';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  isLoading?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  isLoading = false,
}: ConfirmDialogProps) {
  // Close on Escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !isLoading) onClose();
  }, [onClose, isLoading]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      onClick={() => { if (!isLoading) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'confirmFadeIn 200ms ease',
      }}
    >
      <style>{`
        @keyframes confirmFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes confirmScaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 320,
          width: '100%',
          background: T.white,
          borderRadius: 20,
          padding: '28px 24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          textAlign: 'center',
          animation: `confirmScaleIn 200ms ${T.easeEntrance}`,
          willChange: 'transform, opacity',
        }}
      >
        <div style={{
          fontSize: 17,
          fontWeight: 600,
          color: T.text,
          fontFamily: T.fontFamily,
        }}>
          {title}
        </div>

        <div style={{
          fontSize: 14,
          color: T.textSecondary,
          fontFamily: T.fontFamily,
          marginTop: 8,
          lineHeight: 1.45,
        }}>
          {description}
        </div>

        <button
          onClick={() => { if (!isLoading) onConfirm(); }}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: 14,
            borderRadius: 26,
            border: 'none',
            background: destructive ? '#D64545' : T.primary,
            color: T.white,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: T.fontFamily,
            cursor: isLoading ? 'default' : 'pointer',
            marginTop: 24,
            opacity: isLoading ? 0.7 : 1,
            transition: 'opacity 150ms ease',
          }}
        >
          {isLoading ? 'Processing...' : confirmLabel}
        </button>

        <button
          onClick={() => { if (!isLoading) onClose(); }}
          style={{
            width: '100%',
            padding: 10,
            marginTop: 4,
            background: 'none',
            border: 'none',
            color: T.textSecondary,
            fontSize: 14,
            fontWeight: 500,
            fontFamily: T.fontFamily,
            cursor: 'pointer',
          }}
        >
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}
